import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { CSSTransition } from 'react-transition-group'
import {
  changeCreating,
  changeDeleting,
  changeEditing,
  changeFormData,
  changeModal,
  changeServerErrors,
  fetchSchemaIfNeeded,
  getArts,
  fetchTenantsIfNeeded,
  fetchTenantsType,
  fetchWhitelistIfNeeded,
  receiveSchema,
  getTenantRoles,
  selectTenant,
  startSaving,
} from '../../../data/actions'
import {
  isUserTenantEditor,
  scrollTop,
  tenantErrorMessage,
} from '../../../util'
import { animationSettings } from '../../../config'
import Tenant from './components/tenant/Tenant'
import Edit from './components/edit/Edit'
import DeleteConfirmationModalContainer from './components/deleteConfirmationModal/DeleteConfirmationModal'
import TenantMembersDialog from './components/tenantMembersDialog/TenantMembersDialog'
import TenantLogsDialog from './components/tenantLogsDialog/TenantLogsDialog'
import TenantBookingsDialog from './components/tenantBookingsDialog'
import TenantComplianceDialog from './components/tenantComplianceDialog'
import { ChangeProviderDialog } from './components/ChangeProviderDialog'
import InvalidateAkamaiCacheDialog from './components/InvalidateAkamaiCacheDialog'
import { getSchema } from './helpers'

const TenantContainer = ({
  schema,
  arts,
  match,
  changeModal,
  changeDeleting,
  selectedTenant,
  isCreating,
  startSaving,
  changeCreating,
  changeEditing,
  changeServerErrors,
  allTenants,
  sample,
  history,
  isFetching,
  memberExpiringDateRange,
  isEditing,
  isSaving,
  isWhitelisted,
  roles,
  serverErrors,
  username,
  activeModalId,
  selectTenant,
  formData,
}) => {
  const { dialog, acronym } = match.params
  const [activeDialog, setActiveDialog] = useState(null)

  useEffect(() => {
    if ((dialog || null) !== activeDialog) {
      setActiveDialog(dialog)
    }
  }, [dialog])

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0]
    if (activeDialog) {
      body.classList.add('modal-open')
    } else {
      body.classList.remove('modal-open')
    }
    return () => body.classList.remove('modal-open')
  }, [activeDialog])

  const changeDialog = (dialog) => {
    let url = `/apps/${acronym}`
    if (dialog) {
      url += `/${dialog}`
    }
    history.replace(url)
  }

  useEffect(() => {
    selectTenant(acronym)
  }, [acronym])

  const onDeleteConfirmClick = () => {
    changeDeleting(true)
    startSaving(selectedTenant)
    changeModal(null)
  }

  const onSaveClick = (data) => {
    delete data.members
    if (data.experiences?.length === 1 && data.experiences[0] === 'no_exp') {
      data.experiences = []
    }

    if (isCreating) {
      let acronym = (data.acronym = data.acronym.toLowerCase())
      startSaving(acronym, data)
    } else {
      startSaving(selectedTenant, data)
    }

    scrollTop()
  }

  const onCancelClick = () => {
    if (isCreating) {
      changeCreating(false)
    }

    changeEditing(false)
    changeServerErrors(null)
  }

  const tenantInfo = () => {
    if (isCreating) {
      return sample
    }
    return allTenants[selectedTenant] || {}
  }

  const tenantInformation = tenantInfo()
  const isValidTenant =
    tenantInformation && tenantInformation.hasOwnProperty('name')
  const _timeout = animationSettings.timeout

  let setRoles = new Set(roles)
  let hasRolePermission = [...new Set(tenantInformation.type)].filter((x) =>
    setRoles.has(x),
  )
  const isEditable = !!(
    (!isFetching &&
      isValidTenant &&
      (isWhitelisted ||
        isUserTenantEditor(tenantInformation, username) ||
        roles?.includes('admin'))) ||
    hasRolePermission.length
  )

  const hasServerError = !isFetching && serverErrors.hasErrors
  const hasUnselectedError =
    !isFetching && !hasServerError && !selectedTenant?.length
  const errorMsg = tenantErrorMessage(
    isValidTenant,
    hasUnselectedError,
    hasServerError,
    serverErrors,
    selectedTenant,
  )

  const dialogMapper = {
    members: () => (
      <TenantMembersDialog
        tenant={tenantInformation}
        onClose={() => changeDialog()}
      />
    ),
    logs: () => (
      <TenantLogsDialog
        tenant={tenantInformation}
        tenants={Object.keys(allTenants)}
        onClose={() => changeDialog()}
      />
    ),
    bookings: () => <TenantBookingsDialog onClose={() => changeDialog()} />,
    compliance: () => <TenantComplianceDialog onClose={() => changeDialog()} />,
    akamaiCacheInvalidation: () => (
      <InvalidateAkamaiCacheDialog onClose={() => changeDialog()} />
    ),
    changeProvider: () => (
      <ChangeProviderDialog onClose={() => changeDialog()} />
    ),
  }
  return (
    <>
      <CSSTransition
        in={isFetching}
        timeout={_timeout}
        classNames="loader-container"
        unmountOnExit
      >
        <div className="loader-container">
          <div className="loader">
            <wb-spinner mode="dark"></wb-spinner>
          </div>
        </div>
      </CSSTransition>

      <CSSTransition
        in={
          (!isEditing && hasServerError) ||
          (!isFetching && !!selectedTenant && !isValidTenant)
        }
        timeout={_timeout}
        classNames="tenants-info"
        unmountOnExit
      >
        <div className="page-content">
          <div className="page-body page-body__message_box  page-body__message_box-error">
            {errorMsg}
          </div>
        </div>
      </CSSTransition>

      <CSSTransition
        in={!isEditing && hasUnselectedError}
        timeout={_timeout}
        classNames="tenants-info"
        unmountOnExit
      >
        <div className="page-content">
          <div className="page-body page-body__message_box">{errorMsg}</div>
        </div>
      </CSSTransition>

      <CSSTransition
        in={!isEditing && !hasServerError && !isFetching && isValidTenant}
        timeout={_timeout}
        classNames="tenant-content"
        unmountOnExit
      >
        <Tenant
          selectedTenant={selectedTenant}
          arts={arts}
          tenantInfo={{ ...tenantInformation }}
          username={username}
          isEditable={isEditable}
          isWhitelisted={isWhitelisted}
          onEditClick={() => changeEditing(true)}
          onDeleteClick={() => changeModal('DeleteConfirmationModal')}
          onMembersClick={() => changeDialog('members')}
          onLogsClick={() => changeDialog('logs')}
          onBookingsClick={() => changeDialog('bookings')}
          onAkamaiCacheInvalidation={() =>
            changeDialog('akamaiCacheInvalidation')
          }
          onChangeProviderClick={() => changeDialog('changeProvider')}
          userRoles={roles}
          onComplianceInfoClick={() => changeDialog('compliance')}
        />
      </CSSTransition>

      <CSSTransition
        in={isEditable && isEditing}
        timeout={_timeout}
        classNames="tenants-info"
        unmountOnExit
      >
        <Edit
          schema={getSchema({
            tenantInfo: tenantInformation,
            schema,
            arts,
            isCreating,
            sample,
            allTenants,
            isWhitelisted,
            receiveSchema,
          })}
          formData={formData}
          selectedTenant={selectedTenant}
          tenantInfo={{ ...tenantInformation }}
          isEditable={isEditable}
          isCreating={isCreating}
          isSaving={isSaving}
          isWhitelisted={isWhitelisted}
          memberExpiringDateRange={memberExpiringDateRange}
          serverErrors={serverErrors}
          onSaveClick={onSaveClick}
          onCancelClick={onCancelClick}
          onMembersClick={() => changeDialog('members')}
          onLogsClick={() => changeDialog('logs')}
          onBookingsClick={() => changeDialog('bookings')}
          onComplianceInfoClick={() => changeDialog('compliance')}
          userRoles={roles}
        />
      </CSSTransition>

      <CSSTransition
        in={isSaving}
        timeout={_timeout}
        classNames="loader-container"
        unmountOnExit
      >
        <div className="loader-container--saving">
          <div className="loader">
            <wb-spinner mode="dark"></wb-spinner>
          </div>
        </div>
      </CSSTransition>

      <CSSTransition
        in={activeModalId === 'DeleteConfirmationModal'}
        timeout={animationSettings.timeoutInOut}
        classNames="modal_fx"
        unmountOnExit={true}
      >
        <DeleteConfirmationModalContainer
          onDeleteConfirmClick={onDeleteConfirmClick}
          onDeleteCancelClick={() => changeModal(null)}
          onDeleteCloseClick={() => changeModal(null)}
        />
      </CSSTransition>
      {activeDialog && dialogMapper[activeDialog]()}
    </>
  )
}

export default connect(
  ({
    tenants: {
      isFetching,
      apiAccess,
      schema,
      sample,
      allTenants,
      selectedTenant,
      todayDate,
      memberExpiringDateRange,
      isEditable,
      isEditing,
      isCreating,
      isSaving,
      isWhitelisted,
      roles,
      arts,
      serverErrors,
      formData,
    },
    sso: { username },
    modal: { activeModalId },
  }) => ({
    isFetching,
    apiAccess,
    schema,
    arts,
    sample,
    allTenants,
    selectedTenant,
    todayDate,
    memberExpiringDateRange,
    isEditable,
    isEditing,
    isCreating,
    isSaving,
    isWhitelisted,
    roles,
    serverErrors,
    username,
    activeModalId,
    formData,
  }),
  {
    fetchWhitelist: fetchWhitelistIfNeeded,
    fetchTenantsType,
    fetchSchema: fetchSchemaIfNeeded,
    getArts,
    fetchTenants: fetchTenantsIfNeeded,
    changeEditing,
    changeCreating,
    changeDeleting,
    startSaving,
    changeServerErrors,
    changeModal,
    changeFormData,
    receiveSchema,
    selectTenant,
    getTenantRoles,
  },
)(withRouter(TenantContainer))
